import {
  type ComponentType,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import classNames from 'classnames';

import {
  attachComponentData,
  getComponentData,
  useElementFilter,
} from '@backstage/core-plugin-api';

import { Flex } from '@spotify-internal/encore-web';
import { Box } from '@spotify-internal/encore-web/next';

import styles from './FilterBarLayout.module.css';

type FilterBarSlot =
  | 'leading'
  | 'content'
  | 'filters'
  | 'filterActions'
  | 'trailing'
  | 'footer';
const FILTER_BAR_SLOT_KEY = 'filterBar.slot';

export const asFilterBarSlot = <P,>(
  component: ComponentType<P>,
  slot: FilterBarSlot,
): ComponentType<P> => {
  attachComponentData(component, FILTER_BAR_SLOT_KEY, slot);
  return component;
};

export const FilterBarLeading = asFilterBarSlot(
  ({ children }: PropsWithChildren): JSX.Element => <>{children}</>,
  'leading',
);
export const FilterBarTrailing = asFilterBarSlot(
  ({ children }: PropsWithChildren): JSX.Element => <>{children}</>,
  'trailing',
);

export const FilterBarLayout = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const regions = useElementFilter(children, elements => {
    const slots: Record<FilterBarSlot, ReactNode[]> = {
      leading: [],
      content: [],
      filters: [],
      filterActions: [],
      trailing: [],
      footer: [],
    };
    elements.getElements<PropsWithChildren>().forEach(element => {
      const slot =
        getComponentData<FilterBarSlot>(element, FILTER_BAR_SLOT_KEY) ??
        'content';
      slots[slot].push(element);
    });
    return slots;
  });

  return (
    <Box className={styles.container}>
      <Box
        className={classNames(styles.layout, {
          [styles.withoutLeading]: regions.leading.length === 0,
        })}
      >
        <Box className={styles.primary}>
          {regions.leading.length > 0 && (
            <Flex
              alignItems="center"
              gap="tighter"
              flexShrink={0}
              className={styles.leading}
            >
              {regions.leading}
            </Flex>
          )}
          <Flex
            alignItems="center"
            gap="tighter-2"
            wrap="wrap"
            flexGrow={1}
            minWidth="0"
            className={styles.content}
          >
            {regions.content}
          </Flex>
          {regions.filters.length > 0 && (
            <Box className={styles.filters}>{regions.filters}</Box>
          )}
        </Box>
        {regions.filterActions.length > 0 && (
          <Flex className={styles.filterActions}>{regions.filterActions}</Flex>
        )}
        {regions.trailing.length > 0 && (
          <Flex
            justifyContent="end"
            minWidth="0"
            maxWidth="100%"
            className={styles.trailing}
          >
            {regions.trailing}
          </Flex>
        )}
        {regions.footer.length > 0 && (
          <Box className={styles.footer}>{regions.footer}</Box>
        )}
      </Box>
    </Box>
  );
};
